import "../dashboardPage/dashboard.page.css";
import "../portal.css";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client";
import { DatePicker, Dropdown, Select, Skeleton, message } from "antd";
import dayjs from "dayjs";
import { Navbar } from "@src/components/navbar";
import { getCustomerPortalRosterQuery, getCustomerPortalServiceSummaryQuery } from "@src/util/query/portal.query";
import { getAllSitesForCustomerQuery } from "@src/util/query/site.query";
import { downloadCsv, downloadServerPdf } from "@src/util/portal/download";
import { formatHours, officerName, sourceLabel, stateMeta, timeRange } from "@src/util/portal/dutyState";

const { RangePicker } = DatePicker;

// Reports - how the service is performing (SLA scorecard, by site, missed duties,
// incidents) for a period, plus downloads: the monthly service report (PDF) and
// the hours report (Excel / PDF). The per-duty reports stay on each duty (Roster)
// and on Schedules & Activity.

export const PERIODS = [
    { key: "thisMonth", label: "This month", range: () => [dayjs().startOf("month"), dayjs().endOf("day")] },
    { key: "lastMonth", label: "Last month", range: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
    { key: "last30", label: "Last 30 days", range: () => [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
];

// 95%+ on target, 85-94% close, below 85% needs attention
export const rateTone = (value) => {
    if (value === null || value === undefined) return "none";
    if (value >= 95) return "good";
    if (value >= 85) return "warn";
    return "bad";
};

export const SCORECARD = [
    { key: "fillRate", label: "Cover filled", sub: (t) => `${t.shiftsFilled} of ${t.shifts} duties had an officer` },
    { key: "attendanceRate", label: "Attendance", sub: (t) => `${t.attended} clocked in · ${t.gpsVerified} GPS verified` },
    { key: "punctuality", label: "Punctuality", sub: (t) => `${t.onTime} of ${t.attended} on time` },
    { key: "hoursDeliveredRate", label: "Hours delivered", sub: (t) => `${formatHours(t.deliveredHours)} of ${formatHours(t.pastScheduledHours)}` },
    { key: "welfareRate", label: "Welfare checks", sub: (t) => (t.checkCallsDue ? `${t.checkCallsDone} of ${t.checkCallsDue} answered` : "None due") },
    { key: "patrolRate", label: "Patrol checkpoints", sub: (t) => (t.checkpointsDue ? `${t.checkpointsDone} of ${t.checkpointsDue} scanned` : "None due") },
];

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v)}%`);

export const ServiceReport = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [customerDetail, setCustomerDetail] = useState(null);
    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const periodKey = params.get("period") || "thisMonth";
    const siteId = params.get("site") || null;
    const custom = periodKey === "custom" && params.get("from") && params.get("to")
        ? [dayjs(params.get("from")).startOf("day"), dayjs(params.get("to")).endOf("day")]
        : null;
    const [from, to] = custom || (PERIODS.find((p) => p.key === periodKey) || PERIODS[0]).range();

    const setParam = (changes) => {
        const next = new URLSearchParams(params);
        Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
        setParams(next, { replace: true });
    };

    const variables = useMemo(() => ({ from: from.toISOString(), to: to.toISOString(), siteId }),
        [from.valueOf(), to.valueOf(), siteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const { data, loading } = useQuery(getCustomerPortalServiceSummaryQuery, { variables, fetchPolicy: "cache-and-network" });
    const { data: sitesData } = useQuery(getAllSitesForCustomerQuery, {
        variables: { customerId: customerDetail?._id },
        skip: !customerDetail?._id,
        fetchPolicy: "cache-first",
    });
    const sites = sitesData?.getAllSiteForCustomer?.response || [];
    const [fetchRoster, { loading: hoursLoading }] = useLazyQuery(getCustomerPortalRosterQuery, { fetchPolicy: "network-only" });

    const summary = data?.getCustomerPortalServiceSummary?.response;
    const t = summary?.totals;
    const isLoading = loading && !summary;
    const periodText = `${from.format("D MMM YYYY")} – ${to.format("D MMM YYYY")}`;

    // PDFs are built by the server and saved straight to downloads (no print dialog)
    const pdfParams = { from: from.toISOString(), to: to.toISOString(), siteId };
    const fileDates = `${from.format("YYYY-MM-DD")}-to-${to.format("YYYY-MM-DD")}`;
    const downloadServicePdf = () => downloadServerPdf("/reports/customer/service.pdf", pdfParams, `service-report-${fileDates}.pdf`);
    const downloadHoursPdf = () => downloadServerPdf("/reports/customer/schedule.pdf", { ...pdfParams, kind: "hours" }, `hours-report-${fileDates}.pdf`);

    const downloadHoursCsv = async () => {
        try {
            const { data: rosterData } = await fetchRoster({ variables });
            const rows = rosterData?.getCustomerPortalRoster?.response?.rows || [];
            if (!rows.length) { message.info("No duties in this period."); return; }
            downloadCsv(
                `hours-${from.format("YYYY-MM-DD")}-to-${to.format("YYYY-MM-DD")}.csv`,
                ["Date", "Site", "Service", "Duty type", "Officer", "Booked start", "Booked end", "Booked hours", "Clock in", "Clock out", "Hours on duty", "Status"],
                rows.map((r) => [
                    dayjs(r.start).format("DD/MM/YYYY"),
                    r.siteName,
                    sourceLabel(r),
                    r.dutyType,
                    officerName(r),
                    dayjs(r.start).format("HH:mm"),
                    r.end ? dayjs(r.end).format("HH:mm") : "",
                    r.hours,
                    r.clockIn ? dayjs(r.clockIn.at).format("DD/MM/YYYY HH:mm") : "",
                    r.clockOut ? dayjs(r.clockOut.at).format("DD/MM/YYYY HH:mm") : "",
                    r.actualHours ?? "",
                    stateMeta(r.state).label,
                ])
            );
        } catch (e) {
            message.error("Could not build the hours report.");
        }
    };

    return (
        <div className="cdb-page">
            <Navbar customerDetail={customerDetail} />

            <div className="cdb-body">
                <header className="cp-page-head">
                    <div>
                        <h1 className="cdb-title">Reports</h1>
                        <div className="cp-page-sub">How your service is performing, and proof of service for your records and audits.</div>
                    </div>
                    <div className="cdb-header-actions">
                        <Dropdown
                            trigger={["click"]}
                            menu={{
                                items: [
                                    { key: "service", label: "Service report (PDF)", onClick: downloadServicePdf },
                                    { key: "hoursPdf", label: "Hours report (PDF)", onClick: downloadHoursPdf },
                                    { key: "hoursCsv", label: hoursLoading ? "Building hours report…" : "Hours report (Excel)", onClick: downloadHoursCsv, disabled: hoursLoading },
                                ],
                            }}
                        >
                            <button type="button" className="cdb-btn cdb-btn-primary">Download ▾</button>
                        </Dropdown>
                    </div>
                </header>

                <div className="cp-toolbar">
                    <div className="cdb-segmented" role="tablist">
                        {PERIODS.map((p) => (
                            <button key={p.key} type="button" role="tab" aria-selected={periodKey === p.key} className={periodKey === p.key ? "active" : ""} onClick={() => setParam({ period: p.key, from: null, to: null })}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <RangePicker
                        className="cp-range"
                        format="D MMM YYYY"
                        value={[from, to]}
                        allowClear={false}
                        disabledDate={(d, info) => d.isAfter(dayjs(), "day") || (info?.from && Math.abs(d.diff(info.from, "day")) > 185)}
                        onChange={(v) => v && setParam({ period: "custom", from: v[0].format("YYYY-MM-DD"), to: v[1].format("YYYY-MM-DD") })}
                    />
                    <Select
                        className="cp-site-select"
                        value={siteId || "all"}
                        onChange={(v) => setParam({ site: v === "all" ? null : v })}
                        showSearch
                        optionFilterProp="label"
                        options={[{ value: "all", label: "All sites" }, ...sites.map((s) => ({ value: s._id, label: s.sitename }))]}
                    />
                </div>

                {isLoading ? (
                    <section className="cdb-card"><div className="cdb-card-body"><Skeleton active paragraph={{ rows: 8 }} /></div></section>
                ) : !t ? null : (
                    <>
                        <div className="cp-score-head">
                            <span><b>{t.shifts}</b> duties · <b>{formatHours(t.scheduledHours)}</b> booked · {periodText}</span>
                            <span className="cp-legend">
                                <i className="cp-dot cp-good" /> 95%+ on target <i className="cp-dot cp-warn" /> 85–94% <i className="cp-dot cp-bad" /> below 85%
                            </span>
                        </div>

                        <div className="cp-score">
                            {SCORECARD.map((s) => (
                                <div key={s.key} className={`cp-score-tile cp-tone-${rateTone(t[s.key])}`}>
                                    <span className="cdb-kpi-label">{s.label}</span>
                                    <span className="cp-score-value">{fmtPct(t[s.key])}</span>
                                    <span className="cdb-kpi-sub">{s.sub(t)}</span>
                                </div>
                            ))}
                            <div className={`cp-score-tile cp-tone-${t.missed ? "bad" : "good"}`}>
                                <span className="cdb-kpi-label">Missed duties</span>
                                <span className="cp-score-value">{t.missed}</span>
                                <span className="cdb-kpi-sub">Pending, missed clock-ins and duties with no officer</span>
                            </div>
                            <button type="button" className="cp-score-tile cp-tone-none cp-score-click" onClick={() => navigate("/customer/incidents")}>
                                <span className="cdb-kpi-label">Incidents</span>
                                <span className="cp-score-value">{t.incidents}</span>
                                <span className="cdb-kpi-sub">Reported on your sites ›</span>
                            </button>
                        </div>

                        <section className="cdb-card">
                            <div className="cdb-card-head"><h3>By site</h3></div>
                            {summary.bySite.length === 0 ? <div className="cdb-empty">No duties in this period.</div> : (
                                <div className="cp-table-wrap">
                                    <table className="cp-table">
                                        <thead>
                                            <tr>
                                                <th>Site</th><th className="cp-num">Duties</th><th className="cp-num">Cover</th><th className="cp-num">Attendance</th>
                                                <th className="cp-num">On time</th><th className="cp-num">Hours delivered</th><th className="cp-num">Welfare</th>
                                                <th className="cp-num">Patrols</th><th className="cp-num">Missed</th><th className="cp-num">Incidents</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.bySite.map((s) => (
                                                <tr key={s.siteId} className="cp-row-click" onClick={() => setParam({ site: s.siteId })}>
                                                    <td><b>{s.siteName}</b></td>
                                                    <td className="cp-num">{s.shifts}</td>
                                                    {["fillRate", "attendanceRate", "punctuality"].map((k) => <td key={k} className={`cp-num cp-text-${rateTone(s[k])}`}>{fmtPct(s[k])}</td>)}
                                                    <td className={`cp-num cp-text-${rateTone(s.hoursDeliveredRate)}`}>{formatHours(s.deliveredHours)} <span className="cdb-muted">/ {formatHours(s.pastScheduledHours)}</span></td>
                                                    {["welfareRate", "patrolRate"].map((k) => <td key={k} className={`cp-num cp-text-${rateTone(s[k])}`}>{fmtPct(s[k])}</td>)}
                                                    <td className={`cp-num${s.missed ? " cp-text-bad" : ""}`}>{s.missed}</td>
                                                    <td className="cp-num">{s.incidents}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <div className="cdb-row cdb-row-halves">
                            <section className="cdb-card">
                                <div className="cdb-card-head"><h3>Missed or uncovered duties</h3></div>
                                <div className="cdb-card-body">
                                    {summary.attention.length === 0 ? <div className="cdb-empty">Every duty in this period was covered.</div> : (
                                        <ul className="cdb-list">
                                            {summary.attention.slice().reverse().map((r) => {
                                                const m = stateMeta(r.state);
                                                return (
                                                    <li key={r._id} className="cdb-list-item">
                                                        <div className="cdb-list-main">
                                                            <div className="cdb-list-title">{r.siteName}</div>
                                                            <div className="cdb-list-sub">{dayjs(r.start).format("ddd D MMM")} · {timeRange(r.start, r.end)} · {r.dutyType || sourceLabel(r)}</div>
                                                        </div>
                                                        <span className="cdb-pill" style={{ color: m.color, borderColor: m.color, background: m.bg }}>{m.label}</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </section>

                            <section className="cdb-card">
                                <div className="cdb-card-head">
                                    <h3>Incidents in this period</h3>
                                    <button type="button" className="cdb-link" onClick={() => navigate("/customer/incidents")}>View all</button>
                                </div>
                                <div className="cdb-card-body">
                                    {summary.incidents.length === 0 ? <div className="cdb-empty">No incidents reported in this period.</div> : (
                                        <ul className="cdb-list">
                                            {summary.incidents.slice(0, 8).map((i) => (
                                                <li key={i._id} className="cdb-list-item cdb-clickable" onClick={() => navigate(`/customer/incidents/${i._id}`)}>
                                                    <div className="cdb-list-main">
                                                        <div className="cdb-list-title">{i.incidentType} <span className="cdb-muted">at</span> {i.siteName}</div>
                                                        <div className="cdb-list-sub">{dayjs(i.createdAt).format("ddd D MMM YYYY, HH:mm")}</div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>
                        </div>
                    </>
                )}

                <section className="cdb-card">
                    <div className="cdb-card-head"><h3>Proof of service</h3></div>
                    <div className="cp-links">
                        <button type="button" className="cp-link-row" onClick={downloadServicePdf}>
                            <b>Service report</b><span>Cover, attendance, punctuality, hours, welfare, patrols and incidents for {periodText}. PDF.</span>
                        </button>
                        <button type="button" className="cp-link-row" onClick={downloadHoursPdf}>
                            <b>Hours report</b><span>Every duty with booked and actual hours, totals by site. PDF, or <span className="cdb-link" onClick={(e) => { e.stopPropagation(); downloadHoursCsv(); }}>Excel</span>.</span>
                        </button>
                        <button type="button" className="cp-link-row" onClick={() => navigate(siteId ? `/customer/reports/${siteId}` : "/customer/reports")}>
                            <b>Schedules & activity logs</b><span>Each site's duties and every clock in, check call, checkpoint and task, with the duty reports.</span>
                        </button>
                        <button type="button" className="cp-link-row" onClick={() => navigate("/customer/incidents")}>
                            <b>Incident reports</b><span>Every incident reported on your sites, with the full report.</span>
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};
